// src/pages/RegistrationFormSettingsPage.js
// Admin UI to control which fields are required on the public event
// registration form. Pairs with routes/registrationFormSettings.js.
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

export default function RegistrationFormSettingsPage() {
  const { isManagerOrAbove } = useAuth();
  const [fields, setFields] = useState(null);
  const [pending, setPending] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get('/registration-form-settings')
      .then(res => setFields(res.data.fields || []))
      .catch(() => toast.error('Failed to load form settings.'));
  };

  useEffect(() => { load(); }, []);

  const toggle = (key, value) => {
    setPending(p => ({ ...p, [key]: value }));
  };

  const effectiveValue = (f) => (pending[f.key] !== undefined ? pending[f.key] : f.value);
  const hasChanges = Object.keys(pending).length > 0
    && fields && fields.some(f => pending[f.key] !== undefined && pending[f.key] !== f.value);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = Object.entries(pending).map(([key, value]) => ({ key, value }));
      const res = await api.patch('/registration-form-settings', { updates });
      setFields(res.data.fields || []);
      setPending({});
      toast.success('Settings saved.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setPending({});

  if (fields === null) {
    return <div className="loading-page"><div className="spinner" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Form Settings</h1>
          <p className="page-subtitle">Control which fields are required on public-facing forms.</p>
        </div>
        {hasChanges && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={handleReset} disabled={saving}>Discard</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <span className="card-title">Public Event Registration</span>
        </div>
        <div className="card-body">
          <p style={{ marginTop: 0, marginBottom: 16, fontSize: '0.875rem', color: 'var(--gray-600)' }}>
            These toggles control the registration form that appears at{' '}
            <code style={{ background: 'var(--gray-100)', padding: '2px 6px', borderRadius: 3, fontSize: '0.8rem' }}>
              portal.njjba.com/embed/events/&lt;id&gt;
            </code>
            {' '}— the form embedded on njjba.com and the form members see when registering for events from the portal.
          </p>
          <div style={{
            background: 'var(--info-bg, #eff6ff)',
            border: '1px solid #bfdbfe',
            padding: '10px 14px',
            borderRadius: 6,
            marginBottom: 16,
            fontSize: '0.85rem',
            color: 'var(--info, #1e40af)',
          }}>
            ℹ Note: First name, last name, and email are always required — they're needed to create the ticket and send the confirmation.
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {fields.map(f => {
              const current = effectiveValue(f);
              const changed = pending[f.key] !== undefined && pending[f.key] !== f.value;
              return (
                <label key={f.key} style={{
                  display: 'flex', gap: 12, alignItems: 'flex-start',
                  cursor: isManagerOrAbove() ? 'pointer' : 'not-allowed',
                  padding: 14, background: changed ? '#fef9c3' : 'var(--gray-50)',
                  borderRadius: 6, border: changed ? '1px solid #facc15' : '1px solid var(--gray-200)',
                  opacity: isManagerOrAbove() ? 1 : 0.7,
                }}>
                  <input
                    type="checkbox"
                    checked={current}
                    onChange={e => toggle(f.key, e.target.checked)}
                    disabled={!isManagerOrAbove()}
                    style={{ marginTop: 3 }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--gray-900)' }}>
                      {f.label}
                      {changed && <span style={{ marginLeft: 8, fontSize: '0.7rem', color: '#a16207', fontWeight: 700 }}>UNSAVED</span>}
                    </div>
                    {f.description && (
                      <div style={{ fontSize: '0.8rem', color: 'var(--gray-600)', marginTop: 2 }}>
                        {f.description}
                      </div>
                    )}
                  </div>
                  <span style={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: current ? '#15803d' : 'var(--gray-500)',
                    background: current ? '#dcfce7' : 'var(--gray-100)',
                    padding: '2px 8px', borderRadius: 10,
                    textTransform: 'uppercase',
                  }}>
                    {current ? 'Required' : 'Optional'}
                  </span>
                </label>
              );
            })}
          </div>

          {!isManagerOrAbove() && (
            <p style={{ marginTop: 14, fontSize: '0.8rem', color: 'var(--gray-500)', fontStyle: 'italic' }}>
              You need manager or higher permissions to change these settings.
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title">Coming soon</span></div>
        <div className="card-body" style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
          More forms will appear here as we add backend controls for them — e.g. staff contact creation,
          member portal profile edits, sponsorship intake. Tell us which one you'd like configurable next.
        </div>
      </div>
    </div>
  );
}
